import type { OmborJump, OmborTab } from "./pages/OmborPage";
import type { SettingsSection } from "./pages/SettingsPage";

export type Tab =
  | "ish_haqi" | "xarajatlar" | "kassa" | "foyda_zarar"
  | "home" | "works" | "tovars" | "qaytarishlar" | "qarzlar" | "clients"
  | "broadcast" | "employees" | "reminders" | "analytics" | "murojaatlar"
  | "settings" | "tariff";

export type DashboardRoute = {
  tab: Tab;
  settingsSection?: SettingsSection;
  omborSection?: OmborTab;
  focusWorkId?: number;
  omborJump?: OmborJump;
};

const SIMPLE_PATHS: Record<Exclude<Tab, "home" | "works" | "tovars" | "qaytarishlar" | "settings" | "tariff">, string> = {
  qarzlar: "/debts", clients: "/clients", broadcast: "/broadcast",
  employees: "/employees", reminders: "/reminders", analytics: "/analytics",
  murojaatlar: "/messages",
  foyda_zarar: "/profit-loss", kassa: "/kassa", ish_haqi: "/payroll", xarajatlar: "/expenses",
};

const PATH_TABS = new Map(Object.entries(SIMPLE_PATHS).map(([tab, path]) => [path, tab as Tab]));
const SETTINGS = new Set<SettingsSection>(["umumiy", "xizmatlar", "filiallar", "baholar", "tarif", "finance"]);
const OMBOR = new Set<OmborTab>(["tovarlar", "xaridlar", "sotuvlar", "qaytarishlar", "chiqim", "jarayonda", "yetkazib"]);

/** Parse only recognised dashboard URLs. null tells the caller to canonicalise to Home. */
export function parseDashboardRoute(location: Pick<Location, "pathname">, state?: unknown): DashboardRoute | null {
  const path = location.pathname.replace(/\/+$/, "") || "/";
  const routeState = (state && typeof state === "object" ? state : {}) as { omborJump?: OmborJump };
  if (path === "/") return { tab: "home" };
  if (path === "/works") return { tab: "works" };
  const work = path.match(/^\/works\/(\d+)$/);
  if (work) return { tab: "works", focusWorkId: Number(work[1]) };
  const settings = path.match(/^\/settings\/([^/]+)$/);
  if (settings && SETTINGS.has(settings[1] as SettingsSection)) {
    const section = settings[1] as SettingsSection;
    return { tab: section === "tarif" ? "tariff" : "settings", settingsSection: section };
  }
  if (path === "/settings") return { tab: "settings", settingsSection: "umumiy" };
  const warehouse = path.match(/^\/warehouse\/([^/]+)$/);
  if (warehouse && OMBOR.has(warehouse[1] as OmborTab)) {
    const section = warehouse[1] as OmborTab;
    return { tab: section === "qaytarishlar" ? "qaytarishlar" : "tovars", omborSection: section, omborJump: routeState.omborJump };
  }
  const tab = PATH_TABS.get(path);
  return tab ? { tab } : null;
}

export function pathForRoute(route: DashboardRoute): string {
  if (route.focusWorkId != null) return `/works/${route.focusWorkId}`;
  if (route.tab === "home") return "/";
  if (route.tab === "works") return "/works";
  if (route.tab === "settings" || route.tab === "tariff") return `/settings/${route.settingsSection ?? (route.tab === "tariff" ? "tarif" : "umumiy")}`;
  if (route.tab === "tovars" || route.tab === "qaytarishlar") return `/warehouse/${route.omborSection ?? (route.tab === "qaytarishlar" ? "qaytarishlar" : "tovarlar")}`;
  return SIMPLE_PATHS[route.tab];
}

/** Feature gates apply equally to clicks, direct entry and restored history entries. */
export function validateDashboardRoute(route: DashboardRoute | null, hidden: ReadonlySet<Tab>): DashboardRoute {
  return !route || hidden.has(route.tab) ? { tab: "home" } : route;
}

export function readDashboardRoute(): DashboardRoute | null {
  return parseDashboardRoute(window.location, window.history.state);
}

export function writeDashboardRoute(route: DashboardRoute, replace = false): void {
  const state = route.omborJump ? { omborJump: route.omborJump } : null;
  window.history[replace ? "replaceState" : "pushState"](state, "", pathForRoute(route));
}
